import React from "react";
// adjust path as needed
import { useState } from "react";
import config from "../../../config/config";
import MoreDetailsModal from "../client-counterparty/MoreDetailsModal";
import { RadioGroup } from "../../ui/RadioGroup";
import { Textarea } from "../../ui/Textarea";
import { SubHead } from "../../ui/SubHead";
import { MemoizedCorporateUBOForm } from "./corporateUBOForm";
import { yesNoOption } from "../../options/options";
import { Input } from "../../ui/Input";
import ErrorModal from "../../ui/ErrorModal";
import { isSectionSkipped } from "../../../utils/navigationHelper";

export const CorporateStructureForm = React.memo(({ formData, handleChange, isReadOnly, onAddUBO, onUpdateUBO, onRemoveUBO, sendMail, isReport, diligenceStatus, onImportPSC }) => {
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [modalType, setModalType] = useState("psc");
  const [modalRawData, setModalRawData] = useState(null);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const formatAddress = (address) => {
    if (!address) return '';

    const { address_line_1, country, locality, postal_code } = address;
    const parts = [address_line_1, country, `${locality} ${postal_code}`].filter(Boolean);
    return parts.join(', ');
  };

  const formatDateOfBirth = (dateOfBirth) => {
    if (!dateOfBirth || !dateOfBirth.month || !dateOfBirth.year) return '';

    return `${dateOfBirth.month}, ${dateOfBirth.year}`;
  };

  const formatNaturesOfControl = (naturesOfControl) => {
    if (!naturesOfControl || !Array.isArray(naturesOfControl)) return '';

    return naturesOfControl.join(', ');
  };

  const handleGetPSCDetails = async () => {
    try {
      const formData = localStorage.getItem("clientFormData");
      const companyId = JSON.parse(formData)?.registrationNumber;

      const res = await fetch(
        `${config["backend-url"]}/get_psc_details_more_fromlink`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ company_number: companyId }),
        }
      );

      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "Failed to fetch details");

      // Save raw items before transformation (needed for import)
      const rawItems = data.items ? [...data.items] : [];

      // Format addresses, date of birth, and natures of control
      if (data.items && Array.isArray(data.items)) {
        data.items = data.items.map(item => {
          const { address, date_of_birth, natures_of_control, ceased, ...itemWithoutObjects } = item;
          return {
            ...itemWithoutObjects,
            ceased: ceased ? "Yes" : "No",
            formattedDateOfBirth: formatDateOfBirth(date_of_birth),
            formattedNaturesOfControl: formatNaturesOfControl(natures_of_control),
            formattedAddress: formatAddress(address)
          };
        });
      }

      setModalData(data);
      setModalRawData(rawItems);
      setModalType("psc");
      setShowDetailsModal(true);

    } catch (err) {
      console.error("Fetch error:", err);
      setErrorMessage("Failed to fetch additional details.");
      setErrorModalOpen(true);
    }
  };

  return (
    <>
      <div className="form_information">
        <h2 style={{ margin: 0, marginBottom: '20px' }}>Corporate Structure</h2>

        {/* Only when the section genuinely does not apply — Simplified AND no data saved.
            Once anything is saved here the section is required, so telling the user it is
            "not required" while the sign-off gate blocks on it would be a lie. */}
        {isSectionSkipped("corporate") && (
          <div style={{
            backgroundColor: "#e7f3ff",
            border: "1px solid #b3d7ff",
            borderRadius: "8px",
            padding: "12px 16px",
            marginBottom: "20px",
            display: "flex",
            alignItems: "center",
            gap: "10px"
          }}>
            <span style={{ fontSize: "18px" }}>ℹ️</span>
            <span style={{ color: "#0066cc", fontSize: "14px" }}>
              Diligence level is <strong>Simplified</strong>. It is not required to fill out entries on this step —
              you may leave it as it is. If you do fill anything in, this section becomes required and must be
              completed and validated before the request can be signed off.
            </span>
          </div>
        )}

        {/* <p><b>Status:</b> {formData.status}</p> */}
        <form>
          <Input
            label="Status"
            type="text"
            name="status"
            isReport={isReport}
            value={formData.status} // [ ] change status to entityStatus
            onChange={handleChange}
            disabled={true}
          />
          <RadioGroup
            label="Have you obtained a certified structure chart for the entity?"
            required={true}
            disabled={isReadOnly}
            isReport={isReport}
            name={"certifiedStructureChart"}
            value={formData.certifiedStructureChart}
            onChange={handleChange}
            options={yesNoOption}
          />



          {formData.certifiedStructureChart === "No" && (
            <RadioGroup
              label={"Have the ultimate beneficial owners (UBOs) been identified and verified at Companies House or an equivalent corporate registry?"}
              name={"isUboIdentified"}
              isReport={isReport}
              value={formData.isUboIdentified}
              disabled={isReadOnly}
              onChange={handleChange}
              options={yesNoOption}
            />
          )}

          <RadioGroup
            label={"Do you have any outstanding concerns regarding the corporate structure or its transparency?"}
            name={"outstandingConcern"}
            value={formData.outstandingConcern}
            disabled={isReadOnly}
            onChange={handleChange}
            options={yesNoOption}
            isReport={isReport}
          />

          {formData.outstandingConcern === "Yes" && (
            <>
              <Textarea
                label={"Please document your concerns in detail and notify the Money Laundering Nominated Officer (MLNO)."}
                name={"concernDetails"}
                placeholder={"Enter your concerns here..."}
                value={formData.concernDetails}
                disabled={isReadOnly}
                onChange={handleChange}
                isReport={isReport}
              />
              <button
                type="button"
                style={{
                  marginTop: "10px",
                  padding: "8px",
                  border: "none",
                  background: "#07b1b0",
                  color: "white",
                  borderRadius: "4px",
                  cursor: "pointer",
                  float: "left"
                }}
                disabled={isReadOnly || !formData.concernDetails?.trim()}
                onClick={() =>
                  sendMail("MLNO", "There is an issue regarding the corporate structure that requires your attention", formData.concernDetails)
                }
              >
                Notify Your MLNO
              </button>
              <div style={{ clear: "both" }}></div> {/* ⬅️ clears float + spacing */}
            </>
          )}

          {/* ✅ Always on a new line */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: '10px' }}>
            <SubHead text={"Ultimate Beneficial Owner(s) Identification"} isReport={isReport} />
            {!isReport && (
              <button
                type="button"
                onClick={handleGetPSCDetails}
                disabled={isReadOnly}
                style={{
                  background: "#07b1b0",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "14px",
                  color: "#fff",
                  padding: "10px",
                  borderRadius: "10px",
                  flexShrink: 0,
                }}
              >
                Get PSC Detail
              </button>
            )}
          </div>


          <p>You should identify +25% ultimate beneficial owners by reference to your certified structure chart</p>

          <RadioGroup
            label={"Have you identified any +25% Ultimate Beneficial Owners of the entity?"}
            name={"isUbo25Identified"}
            value={formData.isUbo25Identified}
            disabled={isReadOnly}
            onChange={handleChange}
            options={yesNoOption}
            isReport={isReport}
          />

          {/* Show dropdown if Yes is selected */}
          {(formData.isUbo25Identified === "Yes") &&
            <>
              <div className="ubos-section">
                {!isReport && <button
                  type="button"
                  onClick={onAddUBO}
                  disabled={isReadOnly}
                  style={{ padding: "10px 15px", width: "90px", border: "none", background: "#07b1b0", color: "#fff", "fontSize": "12px", "borderRadius": "5px", "cursor": "pointer", "marginTop": "10px", "textAlign": "center" }}
                >
                  Add UBO
                </button>}
                {[...formData.ubos].reverse().map((ubo, index) => (
                  <>
                    <MemoizedCorporateUBOForm
                      key={ubo.id}
                      index={formData.ubos.length - index - 1}
                      onRemoveUBO={onRemoveUBO}
                      uboId={ubo.id}
                      uboData={ubo}
                      onUpdate={onUpdateUBO}
                      isReadOnly={isReadOnly}
                      isReport={isReport}
                    />
                  </>
                ))}
              </div>
            </>}

          {(formData.isUbo25Identified === "No") && (
            <>
              <RadioGroup
                label={"Why have you not identified the +25% ultimate beneficial owners?"}
                name={"whyUboNotIdentified"}
                value={formData.whyUboNotIdentified}
                disabled={isReadOnly}
                onChange={handleChange}
                isReport={isReport}
                options={[
                  {
                    value: "We have not been able to identify the +25% ultimate beneficial owners ",
                    display_value: "We have not been able to identify the +25% ultimate beneficial owners"
                  },
                  {
                    value: "The company knows or has reasonable cause to believe that there is no registrable person or registrable relevant legal entity in relation to the company, i.e., there are no +25% ultimate beneficial owners",
                    display_value: "The company knows or has reasonable cause to believe that there is no registrable person or registrable relevant legal entity in relation to the company, i.e., there are no +25% ultimate beneficial owners"
                  }
                ]}
              />

              <RadioGroup
                label={"Is there one individual who ultimately exercises significant influence or control over this entity - for example, a managing director?"}
                name={"managedByOne"}
                value={formData.managedByOne}
                disabled={isReadOnly}
                onChange={handleChange}
                options={yesNoOption}
                isReport={isReport}
              />


              {formData.managedByOne === "No" && (
                <>
                  <RadioGroup
                    label={"Is it a well known UK Company or a subsidiary of a well-known company registered in the UK or another low risk jurisdiction?"}
                    name={"wellKnownUKCompany"}
                    value={formData.wellKnownUKCompany}
                    disabled={isReadOnly}
                    onChange={handleChange}
                    options={yesNoOption}
                    isReport={isReport}
                  />
                  <RadioGroup
                    label={"Has the contracted entity filed three years or more accounts at UK Companies House? "}
                    name={"threeYearUKAccount"}
                    value={formData.threeYearUKAccount}
                    disabled={isReadOnly}
                    onChange={handleChange}
                    options={yesNoOption}
                    isReport={isReport}
                  />


                </>
              )}
            </>
          )}
          {showDetailsModal && (
            <MoreDetailsModal
              onClose={() => setShowDetailsModal(false)}
              data={modalData}
              type={modalType}
              rawData={modalRawData}
              onImport={(rawItems) => { onImportPSC(rawItems); setShowDetailsModal(false); }}
            />
          )}
        </form>
      </div>
      <ErrorModal isOpen={errorModalOpen} message={errorMessage} onClose={() => setErrorModalOpen(false)} />
    </>
  );
});